import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useNotificationStore, type Notification, type NotificationTone } from '../store/notificationStore'

const TONES: Record<NotificationTone, { card: string; icon: string; link: string }> = {
  info: {
    card: 'border-blue-500/40 bg-blue-500/10',
    icon: 'text-blue-600 dark:text-blue-300',
    link: 'text-blue-600 dark:text-blue-300',
  },
  success: {
    card: 'border-emerald-500/40 bg-emerald-500/10',
    icon: 'text-emerald-600 dark:text-emerald-300',
    link: 'text-emerald-600 dark:text-emerald-300',
  },
  error: {
    card: 'border-red-500/40 bg-red-500/10',
    icon: 'text-red-600 dark:text-red-300',
    link: 'text-red-600 dark:text-red-300',
  },
}

function ToneIcon({ tone, className }: { tone: NotificationTone; className: string }) {
  if (tone === 'success') return <CheckCircleIcon className={className} />
  if (tone === 'error') return <ExclamationTriangleIcon className={className} />
  // Work that is still running, so the icon says running rather than done.
  return <ArrowPathIcon className={`${className} animate-spin`} />
}

function Banner({ n }: { n: Notification }) {
  const dismiss = useNotificationStore((s) => s.dismiss)
  const tone = TONES[n.tone]

  useEffect(() => {
    if (n.autoDismissMs == null) return
    const t = setTimeout(() => dismiss(n.id), n.autoDismissMs)
    return () => clearTimeout(t)
  }, [n.id, n.autoDismissMs, dismiss])

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 bg-theme-card shadow-lg ${tone.card}`}
    >
      <ToneIcon tone={n.tone} className={`mt-0.5 h-5 w-5 shrink-0 ${tone.icon}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-theme-text-primary">{n.message}</p>
        {n.linkTo && (
          <Link
            to={n.linkTo}
            onClick={() => dismiss(n.id)}
            className={`mt-1 inline-block text-sm font-medium underline-offset-2 hover:underline ${tone.link}`}
          >
            {n.linkLabel ?? 'View'}
          </Link>
        )}
        {!n.linkTo && n.onAction && (
          <button
            type="button"
            onClick={() => {
              n.onAction?.()
              dismiss(n.id)
            }}
            className={`mt-1 inline-block text-sm font-medium underline-offset-2 hover:underline ${tone.link}`}
          >
            {n.actionLabel ?? 'View'}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(n.id)}
        aria-label="Dismiss"
        className="shrink-0 text-theme-text-tertiary hover:text-theme-text-primary"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

/**
 * The console's banners, stacked under the header.
 *
 * Fixed rather than in the document flow so raising one never reflows the page underneath it, and
 * above the modal layer (z-50) so a dialog on its way out cannot cover what it just raised.
 */
export default function NotificationBanner() {
  const items = useNotificationStore((s) => s.items)
  if (!items.length) return null
  return (
    <div className="pointer-events-none fixed inset-x-0 top-20 z-[60] flex flex-col items-center gap-2 px-4">
      {items.map((n) => (
        <div key={n.id} className="w-full max-w-md">
          <Banner n={n} />
        </div>
      ))}
    </div>
  )
}
